import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Select,
} from 'antd';
import { CloudSyncOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import axios from 'axios';

import WorkflowHandle from './WorkflowHandle';
import WorkflowActionNode from './WorkflowActionNode';
import WorkflowQuickBooksConfigureDrawer from './WorkflowQuickBooksConfigureDrawer';
import useToggle from '../../common/hooks/useToggle';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import { getType, updateData } from './workflowHelpers';

const INCORRECT_TYPE_TEXT = 'This step only works with Invoice or PO forms';

const getDisabledText = (t) => ({
  Invoice: `This step requires at least one text, material table, or costcode table field and one ${t('Customer')} Select field`,
  PO: 'This step requires at least one Material Table field and one Vendor Select field',
});

const SUPPORTED_FORM_TYPES = new Set([
  'Invoice',
  'PO',
]);

const INVOICE_ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires the following fields to be configured: <br />
      - <b>Customer Field Mapping</b> (set via <b>Configure</b>) <br />
      - <b>Total Field Mapping</b> (set via <b>Configure</b>) <b>OR</b> <br />
      <b>Material Table</b> or <b>Cost Code Table</b> (set via <b>Configure</b>)
    </div>
  ),
};

const PO_ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires the following fields to be configured: <br />
      - <b>PO Value Field Mapping</b> (set via <b>Configure</b>) <br />
      - <b>Vendor Field Mapping</b> (set via <b>Configure</b>) <br />
      - <b>Materials Field Mapping</b> (set via <b>Configure</b>)
    </div>
  ),
};

export default ({
  setDataMap,
  setElements,
  isDisplay,
  name,
  sections = [],
  formTypeName,
  divisionId,
  setDisableNext,
} = {}) => {
  const WorkflowQuickBookSync = function WorkflowQuickBookSync({
    id,
    draggable,
    data = {},
    disabled,
  }) {
    const { t } = useTranslation();

    const {
      taxCode: initialTaxCode,
      costcodeTables: initialCostcodeTables = [],
      materialTables: initialMaterialTables = [],
    } = data;

    const {
      fieldMappings: initialFieldMappings = {},
    } = data;

    const { connectedToQuickbooks = false } = useSelector((state) => state.settings.company);

    const [fieldMappings, setFieldMappings] = useState(initialFieldMappings);
    const [selectedTaxCode, setSelectedTaxCode] = useState(initialTaxCode);
    const [taxCodesMap, setTaxCodesMap] = useState({});
    const [selectedCostcodeTables, setSelectedCostcodeTables] = useState(initialCostcodeTables);
    const [selectedMaterialTables, setSelectedMaterialTables] = useState(initialMaterialTables);

    const disableNextForInvoice = useMemo(() => (
      !fieldMappings?.customer
      || (
        !fieldMappings?.total
        && selectedMaterialTables.length === 0
        && selectedCostcodeTables.length === 0
      )
    ), [fieldMappings, selectedMaterialTables, selectedCostcodeTables]);

    const disableNextForPO = useMemo(() => (
      !fieldMappings?.poValue || !fieldMappings?.vendor || !fieldMappings?.materials
    ), [fieldMappings]);

    useEffect(() => {
      if (!setDisableNext) return;

      const disableNext = (disableNextForInvoice && formTypeName === 'Invoice') || (disableNextForPO && formTypeName === 'PO');
      setDisableNext((prev) => ({ ...prev, [id]: disableNext }));
    }, [setDisableNext, id, disableNextForInvoice, disableNextForPO, formTypeName]);

    useEffect(() => {
      const getTaxCodes = async () => {
        try {
          const { data: newTaxCodes = {} } = await axios.get('/quickbooks/taxcodes');
          setTaxCodesMap(newTaxCodes);
        } catch (err) {
          // silent fail
        }
      };

      if (connectedToQuickbooks) {
        getTaxCodes();
      }
    }, [connectedToQuickbooks]);

    const setMap = {
      taxCode: setSelectedTaxCode,
      costcodeTables: setSelectedCostcodeTables,
      materialTables: setSelectedMaterialTables,
    };

    const onTaxChange = useCallback((newValue) => {
      if (!setDataMap || !id) return;
      setMap.taxCode(newValue);
      setDataMap(updateData(id, { taxCode: newValue }));
    }, [id, setDataMap]);

    const onTableChange = useCallback((key) => (newTables) => {
      if (!setDataMap || !id) return;
      setMap[key](newTables);
      setDataMap(updateData(id, { [key]: newTables }));
    }, [setDataMap, id]);

    const onFieldMappingsChange = useCallback((newMappings) => {
      if (!setDataMap || !id) return;
      setFieldMappings(newMappings);
      setDataMap(updateData(id, { fieldMappings: newMappings }));
    }, [setDataMap, id]);

    const DISABLED_TEXT = getDisabledText(t);
    const style = useMemo(() => {
      if (disabled) return { opacity: 0.7 };
      return draggable ? {} : { width: 220 };
    }, [disabled, draggable]);

    const hover = useMemo(() => {
      if (!SUPPORTED_FORM_TYPES.has(formTypeName)) return INCORRECT_TYPE_TEXT;
      return disabled
        ? DISABLED_TEXT[formTypeName]
        : `Use this step to sync a ${formTypeName} with QuickBooks`;
    }, [disabled, formTypeName]);

    const {
      isToggled: isMappingDrawerVisible,
      toggle: toggleMappingDrawer,
    } = useToggle();

    const isInvoice = formTypeName === 'Invoice';

    const {
      defaultTaxCode,
      taxCodeOptions,
    } = useMemo(() => {
      if (!divisionId || !connectedToQuickbooks) {
        return {
          defaultTaxCode: null,
          taxCodeOptions: [],
        };
      }

      const options = taxCodesMap?.[divisionId] || [];
      const defaultTax = options.find(({ isDefault }) => isDefault);

      return {
        defaultTaxCode: defaultTax ? { label: defaultTax.Name, value: defaultTax.Id } : null,
        taxCodeOptions: options,
      };
    }, [divisionId, connectedToQuickbooks, taxCodesMap]);

    useEffect(() => {
      if (defaultTaxCode && !selectedTaxCode) {
        setSelectedTaxCode(defaultTaxCode);
      }
    }, [defaultTaxCode, selectedTaxCode]);

    const {
      materialTableFields,
      costcodeTableFields,
    } = useMemo(() => (
      sections.reduce((acc, section) => {
        const { fields = [] } = section;
        fields.forEach((field) => {
          const { id: fieldId, configProps: { title: fieldTitle } = {} } = field;
          const type = getType(field);
          const fieldObj = { label: fieldTitle, value: fieldId };
          if (type === 'table - Materials') {
            acc.materialTableFields.push(fieldObj);
          } else if (type === 'table - CostCodes') {
            acc.costcodeTableFields.push(fieldObj);
          }
        });
        return acc;
      }, {
        materialTableFields: [],
        costcodeTableFields: [],
      })
    ), [sections]);

    const errorDetails = useMemo(() => {
      if (formTypeName === 'Invoice' && disableNextForInvoice) return INVOICE_ERROR_DETAILS;
      if (formTypeName === 'PO' && disableNextForPO) return PO_ERROR_DETAILS;
      return undefined;
    }, [formTypeName, disableNextForInvoice, disableNextForPO]);

    return (
      <WorkflowActionNode
        id={id}
        title="Sync with Quickbooks"
        Icon={CloudSyncOutlined}
        type={isInvoice ? 'qbInvoice' : 'qbPO'}
        draggable={draggable}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        isDisplay={isDisplay || disabled}
        hover={hover}
        style={style}
        errorPopover={errorDetails}
      >
        {!draggable && (
          <div>
            {isInvoice && (
              <>
                <Row style={{ marginTop: 10 }}>
                  Select Tax Code
                </Row>
                <Row style={{ margin: '10px 0px' }}>
                  <Select
                    onChange={onTaxChange}
                    value={selectedTaxCode}
                    showSearch
                    optionFilterProp="label"
                    options={taxCodeOptions.map(({ Id, Name }) => ({ value: Id, label: Name }))}
                    labelInValue
                  />
                </Row>
                <Row style={{ marginTop: 10 }}>
                  Select Material Table Field(s):
                </Row>
                <Row style={{ margin: '10px 0px' }}>
                  <Select
                    mode="multiple"
                    onChange={onTableChange('materialTables')}
                    value={selectedMaterialTables}
                    showSearch
                    optionFilterProp="label"
                    options={materialTableFields}
                  />
                </Row>
                <Row style={{ marginTop: 10 }}>
                  Select Costcode Table Field(s):
                </Row>
                <Row style={{ margin: '10px 0px' }}>
                  <Select
                    mode="multiple"
                    onChange={onTableChange('costcodeTables')}
                    value={selectedCostcodeTables}
                    showSearch
                    optionFilterProp="label"
                    options={costcodeTableFields}
                  />
                </Row>
              </>
            )}
            <Row style={{ margin: '20px 0px' }}>
              <BorderlessButton
                title="Configure"
                style={{ margin: 5 }}
                iconNode={<SettingOutlined />}
                onClick={toggleMappingDrawer}
              />
            </Row>
            <WorkflowQuickBooksConfigureDrawer
              visible={isMappingDrawerVisible}
              onClose={toggleMappingDrawer}
              onSubmit={toggleMappingDrawer}
              sourceName={name}
              sourceSections={sections}
              fieldMappings={fieldMappings}
              onFieldMappingsChange={onFieldMappingsChange}
              formTypeName={formTypeName}
              hasTableSelected={(
                selectedMaterialTables.length > 0 || selectedCostcodeTables.length > 0
              )}
            />
            <WorkflowHandle type="target" position="top" disabled={isDisplay} />
            {isInvoice && (
              <>
                <Row justify="center" style={{ marginBottom: 5 }}>
                  <Col>
                    On Payment
                  </Col>
                </Row>
                <WorkflowHandle type="source" position="bottom" disabled={isDisplay} />
              </>
            )}
          </div>
        )}
      </WorkflowActionNode>
    );
  };
  WorkflowQuickBookSync.propTypes = {
    id: PropTypes.string.isRequired,
    draggable: PropTypes.bool,
    disabled: PropTypes.bool,
    data: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  };

  WorkflowQuickBookSync.defaultProps = {
    draggable: false,
    disabled: false,
    data: {},
  };
  return WorkflowQuickBookSync;
};
